import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import * as xlsx from "xlsx";
import { request } from "../../../common/api";
import { currentShipState } from "../../../common/states/ship";
import { ModalIOList } from "../../molecules/Modals/ModalIOList";
import { showAlert } from "../Alert";
import {
  GET_EQUIPMENT_INFO,
  GET_EQUIPMENT_INFO_FILE,
  GET_LOW_MACHINERY_SELECT_OPTIONS,
} from "../../../common/query-keys";
import { EquipmentInfoUpperGridSearchInputs } from "../../../pages/setting/EquipmentInfoPage";
import { ModalLoading } from "../../molecules/Modals/ModalLoading";
import { useState } from "react";

interface EquipmentProcess {
  ssrMachine: string;
  ssrMachineName: string;
  ssrCode: string;
  ssrName: string;
  ssrSignal: string;
  ssrType: string;
  ssrSupplier: string;
  ssrEquipment: string;
  ssrAlarm: number;
  ssrMin: number;
  ssrMax: number;
  ssrLl: number;
  ssrL: number;
  ssrH: number;
  ssrHh: number;
  ssrUnit: string;
  ssrDescription: string;
  ssrDiagnosisTarget: string;
}
interface EquipmentInfoInputs {
  file: File;
  mcyFMemo: string;
}

interface AddEquipmentInfoModalProps {
  onCloseModal: () => void;
  upperGridSearch: EquipmentInfoUpperGridSearchInputs;
}

export const AddEquipmentInfoModal = ({
  onCloseModal,
  upperGridSearch,
}: AddEquipmentInfoModalProps) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentShip = useRecoilValue(currentShipState);

  const { control, handleSubmit } = useForm<EquipmentInfoInputs>({});

  // 파일 등록 API
  const { mutateAsync: filesUploads, isPending } = useMutation({
    mutationFn: (data: FormData) =>
      request({
        method: "POST",
        url: "/equipment-info",
        data: data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
        .then((res) => {
          console.log("res", res);
          setIsLoading(false);
          showAlert("장비 부호가 등록되었습니다.");
          onCloseModal();
          queryClient.invalidateQueries({
            queryKey: [GET_EQUIPMENT_INFO_FILE],
          });
          queryClient.invalidateQueries({ queryKey: [GET_EQUIPMENT_INFO] });
          queryClient.invalidateQueries({
            queryKey: [GET_LOW_MACHINERY_SELECT_OPTIONS],
          });
        })
        .catch((error) => {
          console.log("error", error);
          setIsLoading(false);
        }),
  });

  const onSubmit = (data: any) => {
    setIsLoading(true);
    const file = data.file;
    const fileMemo = data.mcyFMemo;
    const shipTypeNo = data.shipTypeNo;
    const shipNo = data.shipNo;

    if (file) {
      const reader = new FileReader();
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        if (e.target?.result) {
          const data = new Uint8Array(e.target.result as ArrayBuffer);
          const workbook = xlsx.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = xlsx.utils.sheet_to_json(sheet, {
            header: 1,
          }) as any[][];

          // 컬럼 갯수 체크
          // if (Object.keys(jsonData[0]).length !== 17) {
          //   setIsLoading(false);
          //   return showAlert("컬럼 갯수가 다릅니다. 다시 입력해주세요.");
          // }

          // 데이터가 있는 행만 필터링 (빈 행 제거)
          const filteredData = jsonData
            .slice(2)
            .filter(
              (row: any) =>
                row &&
                row.length > 0 &&
                row.some(
                  (cell: any) =>
                    cell !== undefined && cell !== null && cell !== ""
                )
            );

          const processedData = filteredData.map((row: any) => ({
            ssrMachine: row[0],
            ssrMachineName: row[1],
            ssrCode: row[2],
            ssrName: row[3],
            ssrSignal: row[4],
            ssrType: row[5],
            ssrSupplier: row[6],
            ssrEquipment: row[7],
            ssrAlarm: Number(row[8]),
            ssrMax: row[9],
            ssrMin: row[10],
            ssrLl: row[11],
            ssrL: row[12],
            ssrH: row[13],
            ssrHh: row[14],
            ssrUnit: row[15],
            ssrDescription: row[16],
            ssrDiagnosisTarget: row[17],
          }));

          const { equipments } = processData(processedData);

          console.log("equipments", equipments);
          const formData = new FormData();
          formData.append("files", file);
          formData.append("equipments", JSON.stringify(equipments));
          formData.append("FMemo", fileMemo);
          formData.append("shipTypeNo", currentShip?.shipTypeNo || "");
          formData.append("shipNo", currentShip?.shipNo || "");

          await filesUploads(formData);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const processData = (data: EquipmentProcess[]) => {
    const equipments: EquipmentProcess[] = [];

    data.forEach((row) => {
      const {
        ssrMachine,
        ssrMachineName,
        ssrCode,
        ssrName,
        ssrSignal,
        ssrType,
        ssrSupplier,
        ssrEquipment,
        ssrAlarm,
        ssrMin,
        ssrMax,
        ssrLl,
        ssrL,
        ssrH,
        ssrHh,
        ssrUnit,
        ssrDescription,
        ssrDiagnosisTarget,
      } = row;

      const equipmentInfo = {
        ssrMachine,
        ssrMachineName,
        ssrCode,
        ssrName,
        ssrSignal,
        ssrType,
        ssrSupplier,
        ssrEquipment,
        ssrAlarm,
        ssrMin,
        ssrMax,
        ssrLl,
        ssrL,
        ssrH,
        ssrHh,
        ssrUnit,
        ssrDescription,
        ssrDiagnosisTarget,
      };

      equipments.push(equipmentInfo);
    });

    return { equipments };
  };

  return (
    <>
      {isLoading ? (
        <ModalLoading
          modalTitle="파일 업로드"
          modalText="파일을 업로드 중입니다."
        />
      ) : (
        <ModalIOList
          formId="create_iolist"
          control={control}
          onCloseModal={onCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          isPending={isPending}
        />
      )}
    </>
  );
};
